import {
  addDays,
  addWeeks,
  differenceInCalendarDays,
  endOfWeek,
  format,
  getHours,
  getISODay,
  getMinutes,
  getSeconds,
  isBefore,
  isSameMonth,
  isWeekend,
  setHours,
  setMinutes,
  setSeconds,
  startOfWeek,
  sub,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { i18n } from '@/plugins/i18n';
import { WEEKDAYS_NUMBERS } from '@/constants';
import { UserStatistics } from '@/store/modules/StatisticsModule';
import { WeekBoundary } from '@/store/modules/HeatmapModule';
import { Crontab } from '@/store/modules/SettingsModule';

const { t } = i18n.global;

const MONDAY_WEEK_OFFSET = 1;
const FEEDBACK_HEATMAP_WEEK_RANGE = 10;
const DATE_FORMAT = 'yyyy-MM-dd';
const MONTH_FORMAT = 'yyyy-MM';

const localeDateToUtc = (date: Date) => {
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());

  return date;
};

const getStartOfWeek = (date: Date) => {
  const start = startOfWeek(date, { weekStartsOn: MONDAY_WEEK_OFFSET });

  return localeDateToUtc(start);
};

const getEndOfWeek = (date: Date) => {
  const end = endOfWeek(date, { weekStartsOn: MONDAY_WEEK_OFFSET });

  return localeDateToUtc(end);
};

export const getNextMonday = (dateFormat: string, date: string | Date = new Date()): string => {
  const fromDate = new Date(date);
  const week = 7;
  const offsetDays = week - (getISODay(fromDate) - WEEKDAYS_NUMBERS.MONDAY);
  return format(addDays(fromDate, offsetDays), dateFormat);
};

export const setCurrentTimeToDate = (date: Date): Date => {
  const currentDate = new Date();
  return setSeconds(
    setMinutes(setHours(date, getHours(currentDate)), getMinutes(currentDate)),
    getSeconds(currentDate),
  );
};

export const getClosesMonday = (date: string, dateFormat = 'yyyy/MM/dd'): string => {
  const fromDate = new Date(date);
  return getISODay(fromDate) === WEEKDAYS_NUMBERS.MONDAY
    ? format(fromDate, dateFormat)
    : getNextMonday(dateFormat, fromDate);
};

export const getToday = (dateFormat = 'yyyy/MM/dd'): string => {
  return format(new Date(), dateFormat);
};

export const getFeedbackHeatmapWeeks = (crontab: Crontab): WeekBoundary[] => {
  const weeksBoundaries = getFeedbackHeatmapRange(crontab);
  const weeks: WeekBoundary[] = [];
  const firstDay = new Date(weeksBoundaries.firstDay);

  for (let weekIndex = 0; weekIndex <= FEEDBACK_HEATMAP_WEEK_RANGE; weekIndex++) {
    const weekStart = addWeeks(firstDay, weekIndex);
    const weekEnd = getEndOfWeek(weekStart);

    weeks.push({
      weekStart: localeDateToUtc(weekStart),
      weekEnd,
    });
  }

  return weeks;
};

export const getFeedbackHeatmapRange = (crontab: Crontab) => {
  const now = new Date();

  let weekCorrection = 0;
  const [hours, minutes] = crontab.time.split(':').map((value) => Number.parseInt(value));
  if (now.getDay() <= crontab.weekday && now.getHours() * 60 + now.getMinutes() <= hours * 60 + minutes) {
    weekCorrection = 1;
  }

  const startOfLastWeek = getStartOfWeek(now);
  const firstDay = sub(startOfLastWeek, {
    weeks: FEEDBACK_HEATMAP_WEEK_RANGE + weekCorrection,
  });
  const endOfLastWeek = getEndOfWeek(now);
  const lastDay = sub(endOfLastWeek, {
    weeks: weekCorrection,
  });

  return {
    firstDay: localeDateToUtc(firstDay),
    lastDay: lastDay,
  };
};

export const isThisMonth = (date: string): boolean => {
  return isSameMonth(new Date(date), new Date());
};

export const formatDateForTemplate = ({
  date,
  hideYear = false,
  short = false,
  showTime = false,
}: {
  date: string;
  short?: boolean;
  hideYear?: boolean;
  showTime?: boolean;
}): string => {
  const d = new Date(date);
  let dateFormat = short ? 'd. M.' : 'dd. MM.';
  if (!hideYear) {
    dateFormat += short ? ' yy' : ' yyyy';
  }

  if (showTime) {
    dateFormat = `HH:mm ${dateFormat}`;
  }
  return format(d, dateFormat);
};

export const differenceInBusinessDays = (start: string, end: string): number => {
  let dateStart = new Date(start);
  const dateEnd = new Date(addDays(new Date(end), 1));
  const daysDifference = differenceInCalendarDays(dateEnd, dateStart);

  let weekends = 0;
  while (isBefore(dateStart, dateEnd)) {
    weekends += isWeekend(dateStart) ? 1 : 0;
    dateStart = addDays(dateStart, 1);
  }

  return daysDifference - weekends;
};

export const PaginationTranslate = (start: number, end: number, total: number): string => {
  return t('pagination.showOf', { start: start, end: end, total: total });
};

export const WithoutPaginationTranslate = (_start: number, _end: number, totalRowsNumber: number): string => {
  return t('pagination.showAll', { total: totalRowsNumber });
};
export const formatMinutesToHoursAndMinutes = (timeInMinutes: number): string => {
  let timeFormatted: string;

  if (timeInMinutes < 60) {
    timeFormatted = timeInMinutes + 'm';
  } else if (timeInMinutes % 60 === 0) {
    timeFormatted = timeInMinutes / 60 + 'h';
  } else {
    timeFormatted = `${Math.floor(timeInMinutes / 60)}h ${timeInMinutes % 60}m`;
  }
  return timeFormatted;
};

export const timeSpentToMinutes = (value: string): number => {
  const m = value.substring(value.lastIndexOf(' ') + 1, value.lastIndexOf('m'));
  const h = value.substring(0, value.lastIndexOf('h'));

  return (m ? Number.parseInt(m) : 0) + (h ? Number.parseInt(h) * 60 : 0);
};

export const sortDesByProperty = (property: string, a: UserStatistics, b: UserStatistics): number => {
  if (a[property] < b[property]) {
    return 1;
  } else if (a[property] === b[property]) {
    return 0;
  } else {
    return -1;
  }
};

export const getBestUsersByMonthXp = (array: UserStatistics[], numberOfBestUsers: number): UserStatistics[] => {
  return array.slice(0, numberOfBestUsers);
};

export const getBestUsersByTotalXp = (array: UserStatistics[], numberOfBestUsers: number): UserStatistics[] => {
  return array.slice(0, numberOfBestUsers);
};

export const fromCrontab = (crontab: string): { weekday: number; time: string } => {
  const [minutes, hours, , , weekday] = crontab.split(' ');
  return {
    weekday: Number.parseInt(weekday, 10),
    time: `${hours}:${minutes}`,
  };
};

export const toCrontab = (weekday: number, time: string): string => {
  const [hours, minutes] = time.split(':');
  return `${minutes} ${hours} * * ${weekday}`;
};

export const formatTime = (date: Date | string): string => format(new Date(date), 'HH:mm');

export const yesterday = new Date(subDays(new Date(), 1));

export const normalizeString = (str: string): string => {
  // eslint-disable-next-line sonarjs/duplicates-in-character-class
  return str.normalize('NFKD').replaceAll(/[^\w\s.\-_/]/g, '');
};

export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const convertMinutesToHours = (minutes: number): number => {
  return Math.round((minutes / 60) * 100) / 100;
};

export const convertTimeSpentToHours = (value: string): number => {
  return convertMinutesToHours(timeSpentToMinutes(value));
};

export const getTwoYearsAgoFormattedDate = (): string => {
  return format(subYears(new Date(), 2), DATE_FORMAT);
};

export const getTodayFormattedDate = (): string => {
  return format(new Date(), DATE_FORMAT);
};

export const getTodayFormattedDateMonthOnly = (): string => {
  return format(new Date(), MONTH_FORMAT);
};

export const getPreviousMonthFormattedDateMonthOnly = (): string => {
  return format(subMonths(new Date(), 1), MONTH_FORMAT);
};

export const replaceTrailingSpaces = (str: string): string => {
  return str.replaceAll(/\s+/g, ' ').trim();
};

export const formatXp = (xp: number): string => {
  if (!xp) {
    return '0';
  }
  if (Number.isNaN(xp)) {
    return xp.toString();
  }
  if (xp >= 1000) {
    return `${Math.floor(xp / 1000)}k`;
  }
  return xp.toString();
};
